import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'
import { CourseLmsProfileType } from 'enum'
import CourseForm from 'components/ui/forms/course/CourseForm'
import { IMainState } from 'store/states'
import * as fromActions from 'store/actions/courses.action'
import { NewCourse, courseInitialState } from 'models/course.model'
import Loader from 'components/ui/loader/Loader'
import { OnSubmitValues } from 'components/ui/forms/course/types'
import * as fromAccessActions from 'store/actions/access.action'
import { getSynchronousCourse } from '../utils'
import Wrapper from 'components/reusables/wrapper'

interface Params {
  courseId: string
}

export const Edit: React.FC<any> = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const { courseId } = useParams<Params>()

  const { course } = useSelector(({ courses }: IMainState) => courses)
  const { loading } = useSelector(({ courses }: IMainState) => courses)

  const coursePermission = useSelector(
    ({ access }: IMainState) => access.courses[+courseId]
  )

  useEffect(() => {
    dispatch({ type: fromActions.GET_ADMIN_COURSE, payload: courseId })
    dispatch({ type: fromAccessActions.CAN_COURSE, payload: courseId })
  }, [courseId, dispatch])

  const courseData = useMemo(() => course || courseInitialState, [course])

  const transformLmsProfiles = (lmsProfileId: number, kind: number) => {
    const data = { lmsProfileId, kind }
    if (course && course.coursesLmsProfiles) {
      const lmsProfile = course.coursesLmsProfiles.find(
        ({ lmsProfileId: existsLmsProfileId }) =>
          existsLmsProfileId === lmsProfileId
      )
      if (lmsProfile) {
        return { ...data, id: Number(lmsProfile.id) }
      }
    }
    return data
  }

  const handleSubmit = (values: OnSubmitValues, onlySave: boolean) => {
    let coursesLmsProfilesAttributes: any[] = []

    let auxOptional = values.optionalProfiles || []
    auxOptional = auxOptional.map(({ value: profileId }: any) =>
      transformLmsProfiles(profileId, CourseLmsProfileType.Optional)
    )

    let auxRequired = values.requiredProfiles || []
    auxRequired = auxRequired.map(({ value: profileId }: any) =>
      transformLmsProfiles(profileId, CourseLmsProfileType.Required)
    )

    coursesLmsProfilesAttributes = auxRequired.concat(auxOptional)

    if (course && course.coursesLmsProfiles) {
      const removeLms = course.coursesLmsProfiles
        .map((lmsProfile) => {
          if (
            !coursesLmsProfilesAttributes.some(
              ({ lmsProfileId }) => lmsProfileId === lmsProfile.lmsProfileId
            )
          ) {
            return { ...lmsProfile, _destroy: true }
          }
          return null
        })
        .filter((e) => e)
      if (removeLms.length) {
        coursesLmsProfilesAttributes = [
          ...coursesLmsProfilesAttributes,
          ...removeLms,
        ]
      }
    }

    const synchronousCourse = getSynchronousCourse(values)

    const saveCourse = NewCourse({
      name: values.name,
      description: values.description,
      courseType: values.courseType,
      state: values.state?.value,
      startDate: values.startDate ? String(values.startDate) : '',
      untilDate: values.untilDate ? String(values.untilDate) : '',
      banner: values.banner,
      thumbnail: values.thumbnail,
      coursesLmsProfilesAttributes: coursesLmsProfilesAttributes,
      directChiefApproval: values.directChiefApproval,
      invitationNotification: values.invitationNotification,
      canGenerateCertificate: values.canGenerateCertificate,
      reminderNotification: values.reminderNotification,
      reminderNoStartCourseNotification:
        values.reminderNoStartCourseNotification,
      synchronousCourse,
      hours: values.hours,
      canRestart: values.canRestart,
      integrations: values.integrations,
    })

    dispatch({
      type: fromActions.EDIT_COURSE,
      payload: { course: saveCourse, courseId, onlySave },
    })
  }

  return loading ? (
    <Loader overlay color />
  ) : (
    <Wrapper shadow={true}>
      <h1 className="p-0">{t('administration.courses.edit.title')}</h1>
      <CourseForm
        initialValues={courseData}
        onSubmit={handleSubmit}
        coursePermission={coursePermission}
      />
    </Wrapper>
  )
}

export default Edit
